import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { media } from "@atlaskit/primitives";

import { AgileOverview } from "@/templates/agile-hub/types";

import { AgileCards } from "../agile-cards";
import { useCardProps } from "../navigation-cards";

import { BodyTextContainer } from "./body-text-container";
import { TopicIntroduction } from "./topic-introduction";

const readOnBelowCss = css({
    "textAlign": "center",
    "padding": "40px 0",
    "position": "relative",
    "display": "block",
    "fontWeight": "700",
    "color": "#42526E",
    "fontSize": ".875rem",
    "letterSpacing": "1px",
    "lineHeight": "1.25",
    "marginStartEnd": "40px",
    "marginBlockEnd": "14px",
    "textTransform": "uppercase",
    ":hover": {
        color: "#0065ff"
    },
    [media.below.sm]: {
        display: "none"
    }
});

const continuedCss = css({
    color: "#42526E",
    padding: "40px 0",
    position: "relative",
    textAlign: "center",
    fontSize: ".75rem",
    lineHeight: "2",
    textTransform: "uppercase",
    letterSpacing: "1px",
    [media.below.sm]: {
        display: "none"
    }
});

// FIXME: These styles seem like they are in the wrong place. Shouldn't they be in the rich text component?
// NOTE: Nested selectors are necessary here to be able to style the dangerouslySetInnerHTML blob
/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors */
const mainContentWrapperCss = css({
    "scrollBehavior": "smooth",
    "p": {
        color: "#091E42",
        fontSize: "1.125rem",
        lineHeight: "1.5555555"
    },
    "h2, h3, h4": {
        fontFamily: "var(--bxp-charlie-display-font-family) !important",
        fontWeight: "500",
        color: "#253858"
    },
    "h2": {
        fontSize: "1.5rem",
        lineHeight: "1.3333333",
        [media.above.sm]: {
            fontSize: "2.25rem"
        }
    },
    "h3": {
        fontSize: "1.25rem",
        lineHeight: "1.33333333",
        marginBlockEnd: "16px",
        letterSpacing: ".3px",
        [media.above.sm]: {
            fontSize: "1.5rem"
        }
    },
    "h4": {
        fontSize: "1.25rem",
        lineHeight: "1.2",
        marginBlockEnd: "16px",
        letterSpacing: ".3px"
    }
});
/* eslint-enable @atlaskit/ui-styling-standard/no-nested-selectors */

const ReadOnBelow = () => {
    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        document
            .getElementById("continued")
            ?.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <div>
            <a
                css={readOnBelowCss}
                href="#continued"
                onClick={handleClick}
                data-event="clicked"
                data-event-component="linkButton"
                data-product-key="jiraSoftware"
            >
                <FormattedMessage
                    id="agile.overview.readOnBelow"
                    defaultMessage="Read on below"
                />
            </a>
        </div>
    );
};

const Continued = () => {
    return (
        <div css={continuedCss} id="continued">
            <span>
                <FormattedMessage
                    id="agile.overview.continued"
                    defaultMessage="[Continued]"
                />
            </span>
        </div>
    );
};

export type AgileOverviewMainContentProps = {
    topicOverviewData: AgileOverview;
};

export const AgileOverviewMainContent = (
    props: AgileOverviewMainContentProps
) => {
    const TopicIntroductionSection = () => {
        const topicIntroductionContent =
            props.topicOverviewData.topicIntroduction;

        return topicIntroductionContent?.map((data, idx) => (
            <div key={idx}>
                <TopicIntroduction {...data} />
            </div>
        ));
    };

    const BodyTextContainerSection = () => {
        const bodyTextContent = props.topicOverviewData.bodyTextContainer;
        return bodyTextContent?.map((data, idx) => (
            <div key={idx}>
                <BodyTextContainer {...data} />
            </div>
        ));
    };

    const hasBodyText = !!props.topicOverviewData.bodyTextContainer?.length;

    const featuredContentCardProps = useCardProps(
        props.topicOverviewData.featuredContentContainer || []
    );

    return (
        <div css={mainContentWrapperCss}>
            <TopicIntroductionSection />
            {hasBodyText && <ReadOnBelow />}
            <div css={css({ [media.below.sm]: { display: "none" } })}>
                <AgileCards
                    headerText={props.topicOverviewData.name}
                    cardsProps={featuredContentCardProps}
                />
            </div>
            {hasBodyText && <Continued />}
            <BodyTextContainerSection />
        </div>
    );
};
